import type { PropertyIn, PropertyPath } from '@/scopes/common/domain/types'
import type { PriceTableAnalytics } from '@/scopes/prices/domain/types'

import type { StoreApi } from 'zustand'
import { defaultFilterSelection, type FilterSelection } from '@/domain/prices/filters/types'
import { createPricesFiltersParser } from '@/infrastructure/prices/pricesFiltersParser'

import { createClientQueryStringZustandMiddleware } from '@/infrastructure/prices/queryStringZustandMiddleware'
import { setIn } from '@/scopes/common/domain/functions/objectDeepOperations'
import { createBrowserQueryStringKeepingParams } from '@/scopes/common/infrastructure/query-strings/browserQueryStringKeepingParams'
import { createQueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import { createStore } from 'zustand'

export interface PricesState {
  analytics: PriceTableAnalytics | null
  filterSelection: FilterSelection
  selectedPriceId: string | null
  selectedRooms: Record<string, string | null> | null
  selectedInboundRouteId: string | null
  selectedOutboundRouteId: string | null
  createBookingDraft: {
    loading: boolean
    error: boolean
    success: boolean
  }
}

type PriceStatePath = PropertyPath<PricesState>

export interface PricesStoreActions {
  setIn: (path: PriceStatePath, value: PropertyIn<PricesState, typeof path>) => void
  setFilterSelection: <K extends keyof FilterSelection>(filterType: K, value: FilterSelection[K]) => void
  setFiltersSelection: (filterSelection: FilterSelection) => void
  setSelectedPriceId: (priceId: string) => void
  setSelectedRoom: (roomNumber: string, roomId: string | null) => void
  setOutboundRouteId: (routeId: string | null) => void
  setInboundRouteId: (routeId: string | null) => void
}

const initialState = {
  analytics: null,
  createBookingDraft: {
    loading: false,
    error: false,
    success: false,
  },
  filterSelection: defaultFilterSelection,
  selectedRooms: {},
  selectedPriceId: null,
  selectedInboundRouteId: null,
  selectedOutboundRouteId: null,
}

export type PricesStore = PricesState & PricesStoreActions
export type PriceStoreApi = Pick<StoreApi<PricesStore>, 'getState'>

const syncQueryString = createClientQueryStringZustandMiddleware({
  queryStringSerializer: createQueryStringSerializer({
    parser: createPricesFiltersParser(),
  }),
  queryParametersToStateMapper: {
    toState: queryParameters => ({
      filterSelection: queryParameters,
    }),
    toParameters: ({ filterSelection }) => filterSelection,
  },
  browserQueryString: createBrowserQueryStringKeepingParams({}),
})

export function createPricesStore(initState: PricesState): StoreApi<PricesStore> {
  return createStore<PricesStore>()(
    syncQueryString(set => ({
      ...initState,
      setIn: (path, value) => {
        set(state => setIn(state, path, value) as PricesState)
      },
      setFiltersSelection: (filterSelection: FilterSelection) => {
        set({ filterSelection, selectedPriceId: null, selectedRooms: null })
      },
      setFilterSelection: <K extends keyof FilterSelection>(filterType: K, value: FilterSelection[K]) => {
        set(({ filterSelection, selectedPriceId }) => {
          const oldValue = filterSelection[filterType]
          const hasChanged = JSON.stringify(oldValue) !== JSON.stringify(value)

          if (!hasChanged)
            return { filterSelection, selectedPriceId }

          return {
            filterSelection: {
              ...filterSelection,
              [filterType]: value,
            },
            selectedPriceId: null,
            selectedRooms: null,
          }
        })
      },
      setSelectedPriceId: (newPriceId: string) => {
        set(({ selectedPriceId, selectedRooms }) => {
          if (selectedPriceId === newPriceId)
            return { selectedPriceId, selectedRooms }
          return {
            selectedPriceId: newPriceId,
            selectedRooms: null,
          }
        })
      },
      setSelectedRoom: (roomNumber: string, roomId: string | null) => {
        set(({ selectedRooms }) => {
          return {
            selectedRooms: {
              ...selectedRooms,
              [roomNumber]: roomId,
            },
          }
        })
      },
      setInboundRouteId: (routeId: string | null) => {
        set({ selectedInboundRouteId: routeId })
      },
      setOutboundRouteId: (routeId: string | null) => {
        set({ selectedOutboundRouteId: routeId })
      },
    })),
  )
}

export const pricesStore = createPricesStore(initialState)
